import { useEffect, useRef, useState } from "react"
import { useReloadKey } from "./useReloadKey"

/**
 * This hook triggers an update x milliseconds after the first change.
 * If changes keep happening, it doesn't reset the timer.
 * So the update happens x milliseconds after the first change detected.
 */
export function useDebouncedState<T>(defaultValue: T, delayMs = 50) {
  const [_reloadKey, triggerUpdate] = useReloadKey()

  const valueRef = useRef(defaultValue)

  const updateRef = useRef<any>()

  function scheduleUpdate() {
    if (updateRef.current) {
      return
      // clearTimeout(updateRef.current)
    }

    updateRef.current = setTimeout(() => {
      updateRef.current = undefined
      triggerUpdate()
    }, delayMs)
  }

  function setValue(newValue: T) {
    valueRef.current = newValue
    scheduleUpdate()
  }

  return [valueRef.current, setValue] as const
}

// Custom hook
export function useStateDebounced<T>(
  initialValue: T,
  delay: number = 300,
): [T, T, React.Dispatch<React.SetStateAction<T>>] {
  // State and setters for the actual value and the debounced value
  const [value, setValue] = useState<T>(initialValue)
  const [debouncedValue, setDebouncedValue] = useState<T>(initialValue)

  // Ref to hold the timeout ID between renders
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Effect to update the debounced value after a delay
  useEffect(() => {
    // Clear the previous timeout if value or delay changes
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Set a new timeout to update the debounced value after the specified delay
    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    // Cleanup function to clear the timeout if the component unmounts or value/delay changes
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [value, delay])

  // Return the current value, debounced value, and the setter function
  return [value, debouncedValue, setValue]
}
